import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { JobDetail } from '@/components/admin/jobs/job-detail';
import { RetryJobButton } from '@/components/admin/jobs/retry-job-button';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminJob } from '@/hooks/data/admin/use-jobs';
import { ROUTES } from '@/constants/routes';

const paramSchema = z.object({ jobId: z.coerce.number().int().positive() });

export const Route = createFileRoute('/_authenticated/admin/jobs/$jobId')({
  staticData: { title: 'Job' },
  parseParams: (params) => paramSchema.parse(params),
  component: AdminJobDetailPage,
});

function DetailSkeleton(): React.ReactElement {
  return (
    <div className="grid gap-4 px-4 py-6 sm:px-8 @3xl:grid-cols-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton variant="card" key={i} className="h-12 rounded-xl" />
      ))}
    </div>
  );
}

function AdminJobDetailPage(): React.ReactElement {
  const { jobId } = Route.useParams();
  const jobQuery = useAdminJob(jobId);

  const backLink = (
    <Button variant="outline" asChild>
      <Link to={ROUTES.ADMIN_JOBS}>Back</Link>
    </Button>
  );

  if (jobQuery.isLoading) {
    return (
      <main>
        <AdminPageHeader title="Job" actions={backLink} />
        <DetailSkeleton />
      </main>
    );
  }

  if (jobQuery.isError || !jobQuery.data) {
    return (
      <main>
        <AdminPageHeader title="Job" actions={backLink} />
        <div className="px-4 py-12 text-center sm:px-8">
          <p className="text-fg-3 text-fs-13">
            Failed to load job. Please try again.
          </p>
        </div>
      </main>
    );
  }

  const job = jobQuery.data;

  return (
    <main>
      <AdminPageHeader
        title={`Job #${job.id}`}
        description={job.job_type ?? undefined}
        actions={
          <>
            {backLink}
            <RetryJobButton job={job} />
          </>
        }
      />
      <JobDetail job={job} />
    </main>
  );
}
